import type { ComponentType, FC } from 'react'
import { useEffect, useMemo, useState } from 'react'

import {
  ExpandableDescription,
  InfoBox,
  LoaderCircle,
  Radio,
  RadioGroup,
  StyledText,
} from '@skytech/ui'
import { Badge } from '@skytech/ui/Badge'
import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import queries from '@/api/queries'
import { usePolicyId } from '@/components/ecom-policy/hooks/use-policy-id'
import type { GrossSalaryTaxBracket } from '@/components/ecom-policy/types'
import GlobalModal from '@/components/global-modal/GlobalModal'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import Page from '@/components/page/Page'
import { useCart } from '@/contexts/cart/cart-context'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { ecomPolicyResources } from '@/resources/ecom-policy'
import { APP_ROUTES } from '@/util/appRoutes'

import { GreenpoolInfo } from './GreenpoolInfo'
import { AccessoriesProductGroupGrid } from './policy-products/AccessoriesProductGroupGrid'
import { GreenpoolProductsGrid } from './policy-products/greenpool/GreenpoolProductsGrid'
import { ProductGroupGrid } from './policy-products/ProductGroupGrid'
import { PolicyContextProvider, usePolicyContext } from './Policy.context'

const Policy: FC = () => {
  const [isPolicyMessageModalOpen, setIsPolicyMessageModalOpen] = useState(false)
  const [isGrossSalaryTaxBracketModalOpen, setIsGrossSalaryTaxBracketModalOpen] = useState(false)
  const [modalGrossSalaryTaxBracket, setModalGrossSalaryTaxBracket] =
    useState<GrossSalaryTaxBracket>(null)

  const translate = useTranslation()
  const navigate = useNavigate()
  const policyId = usePolicyId()
  const policyCtx = usePolicyContext()
  const [, setGrossSalaryTaxBracket] = policyCtx.grossSalaryTaxBracket
  const { formatNumber } = useFormattedNumberWithCurrency()
  const { getPolicyTermsStatus, addPolicyTermsStatus } = useCart()

  const { data: policy, isLoading } = useQuery(queries.policy(policyId), () =>
    ecomPolicyResources.get(policyId)
  )
  const policyTermsStatus = getPolicyTermsStatus(policyId)

  useEffect(() => {
    if (
      policy?.isLocked ||
      policyTermsStatus?.isTermsAccepted ||
      (!policy?.messageToEmployee && !policy?.coverAmount && !policy?.coverAmountInit)
    )
      return

    setIsPolicyMessageModalOpen(true)
  }, [
    policy?.isLocked,
    policy?.messageToEmployee,
    policy?.coverAmount,
    policy?.coverAmountInit,
    policyTermsStatus?.isTermsAccepted,
  ])

  useEffect(() => {
    if (!policy?.isGrossSalaryScheme || policy?.isLocked) return
    if (policy?.isGrossSalaryScheme && policyTermsStatus?.grossSalaryTaxBracket) {
      setGrossSalaryTaxBracket(policyTermsStatus.grossSalaryTaxBracket)
      return
    }

    setIsGrossSalaryTaxBracketModalOpen(true)
  }, [policy?.isGrossSalaryScheme, policy?.isLocked, policyTermsStatus?.grossSalaryTaxBracket])

  useEffect(() => {
    if (policy?.isLocked && !policy?.isProductViewAllowedWhenLocked) {
      navigate(APP_ROUTES.ecomPolicyList)
    }
  }, [policy, navigate, policyId])

  if (isLoading) return <LoaderCircle />
  if (!policy) return null

  const isInitialCoverAmountUsed =
    (policy.coverAmountInit || 0) > 0 &&
    policy.coverAmountInit !== policy.coverAmount &&
    policy.coverUsed <= 0

  return (
    <Page className="px-2 sm:px-4 md:px-0" pageWrapperClassName="max-w-screen-2xl xl:pr-4">
      <ActiveStep />

      {Boolean(!policy.isLocked && policy.products.length > 0) && (
        <GlobalModal
          isOpen={isPolicyMessageModalOpen}
          ariaLabel="Message to employee"
          className="small message-to-employee-modal"
        >
          <h3>
            <Translation id="Policy vilkårene" />
          </h3>
          <div className="mb-4 flex flex-col gap-4">
            {policy?.messageToEmployee && (
              <p className="my-0 whitespace-pre-wrap text-base">{policy?.messageToEmployee}</p>
            )}

            <StyledText size="large" fontWeight="bold" className="my-0 whitespace-pre-wrap">
              {isInitialCoverAmountUsed ? (
                <Translation
                  id="We cover {coverAmount} for your first order"
                  coverAmount={formatNumber({ amount: policy.coverAmountInit || 0 })}
                />
              ) : policy.coverInterval !== null && policy.coverInterval > 0 ? (
                <Translation
                  id="Your employer covers {coverAmount} every {coverInterval} {coverInterval, plural, one {month} other {months}}. The amount covered for you today is {coverAvailable}. "
                  coverAmount={formatNumber({ amount: policy.coverAmount })}
                  coverInterval={policy.coverInterval}
                  coverAvailable={formatNumber({ amount: policy.coverAmount - policy.coverUsed })}
                />
              ) : (
                <Translation
                  id="Your employer covers {coverAmount}. The amount covered for you today is {coverAvailable}. "
                  coverAmount={formatNumber({ amount: policy.coverAmount })}
                  coverAvailable={formatNumber({ amount: policy.coverAmount - policy.coverUsed })}
                />
              )}
            </StyledText>
          </div>

          <footer className="text-center">
            <Button
              variant="primary"
              onClick={() => {
                addPolicyTermsStatus(policyId, {
                  isTermsAccepted: true,
                  grossSalaryTaxBracket: policy?.isGrossSalaryScheme
                    ? policyTermsStatus?.grossSalaryTaxBracket
                    : undefined,
                })
                setIsPolicyMessageModalOpen(false)
              }}
            >
              <Translation id="Jeg aksepterer vilkårene" />
            </Button>
          </footer>
        </GlobalModal>
      )}

      {Boolean(!policy.isLocked && policy.isGrossSalaryScheme && policy.products.length > 0) && (
        <GlobalModal
          isOpen={isGrossSalaryTaxBracketModalOpen && !isPolicyMessageModalOpen}
          ariaLabel={translate('Gross salary tax bracket')}
        >
          <h3>
            <Translation id="Tax bracket info text" />
          </h3>

          <footer>
            <RadioGroup
              id="gross-salary-tax-bracket"
              label={<Translation id="Are you in the top tax bracket?" />}
            >
              <div className="flex flex-col gap-3 md:flex-row">
                <Radio
                  id="top-tax-bracket"
                  checked={modalGrossSalaryTaxBracket === 'Top'}
                  onChange={() => {
                    setModalGrossSalaryTaxBracket('Top')
                  }}
                  label={<Translation id="Yes" />}
                />

                <Radio
                  id="base-tax-bracket"
                  checked={modalGrossSalaryTaxBracket === 'Base'}
                  onChange={() => {
                    setModalGrossSalaryTaxBracket('Base')
                  }}
                  label={<Translation id="No" />}
                />
              </div>
            </RadioGroup>
            <Button
              variant="primary"
              className="align-self-center ml-0 mt-4"
              onClick={() => {
                setIsGrossSalaryTaxBracketModalOpen(false)
                setGrossSalaryTaxBracket(modalGrossSalaryTaxBracket)
                addPolicyTermsStatus(policyId, {
                  isTermsAccepted: policyTermsStatus?.isTermsAccepted || true,
                  grossSalaryTaxBracket: modalGrossSalaryTaxBracket || undefined,
                })
              }}
              disabled={modalGrossSalaryTaxBracket == null}
            >
              <Translation id="I accept the terms" />
            </Button>
          </footer>
        </GlobalModal>
      )}
    </Page>
  )
}

export const EcomPolicy = () => (
  <PolicyContextProvider>
    <Policy />
  </PolicyContextProvider>
)

const ActiveStep: FC = () => {
  const policyCtx = usePolicyContext()
  const policyId = usePolicyId()
  const {
    getProductsByPolicy,
    getAccessoriesByPolicy,
    qtyOf,
    hasNonGreenpoolItem,
    hasGreenpoolItem,
    getGreenpoolItem,
  } = useCart()

  const [initialLoad, setInitialLoad] = useState(true)

  const [activeStep, setActiveStep] = policyCtx.activeStep
  const [, setOrderTotalCost] = policyCtx.orderTotalCost
  const [, setSelectedProductIds] = policyCtx.selectedProductIds
  const [, setSelectedAccessoriesIds] = policyCtx.selectedAccessoriesIds
  const [, setSelectedGreenpoolProductId] = policyCtx.selectedGreenpoolProductId

  const { data, isLoading } = useQuery(
    queries.policy(policyId),
    () => ecomPolicyResources.get(policyId),
    {
      staleTime: 0,
    }
  )

  const [selectedProductIds, selectedAccessoriesIds, selectedGreenpoolItemId] = useMemo(() => {
    if (data?.isLocked) {
      return [[], [], null]
    }
    const selectedProductIds = getProductsByPolicy(policyId).map(item => Number(item.productId))
    const selectedAccessoriesIds = getAccessoriesByPolicy(policyId).map(item =>
      Number(item.productId)
    )
    const selectedGreenpoolItemId = getGreenpoolItem(policyId)?.productId

    return [selectedProductIds, selectedAccessoriesIds, selectedGreenpoolItemId]
  }, [data, policyId, getProductsByPolicy, getAccessoriesByPolicy, getGreenpoolItem])

  useEffect(() => {
    if (initialLoad && data) {
      setSelectedProductIds(selectedProductIds)
      setSelectedAccessoriesIds(selectedAccessoriesIds)
      if (selectedGreenpoolItemId) {
        setSelectedGreenpoolProductId(Number(selectedGreenpoolItemId))
      }

      setInitialLoad(false)
    }
  }, [
    data,
    initialLoad,
    selectedAccessoriesIds,
    selectedProductIds,
    selectedGreenpoolItemId,
    setSelectedAccessoriesIds,
    setSelectedProductIds,
    setSelectedGreenpoolProductId,
  ])

  useEffect(() => {
    if (!data) return
    const isGreenpoolAvailable = data.greenpoolTypeId && data.greenpoolAssets.length > 0
    const hasSelectedGreenpoolItem = hasGreenpoolItem(policyId)
    const hasSelectedNonGreenpoolItem = hasNonGreenpoolItem(policyId)
    if (!isGreenpoolAvailable || (isGreenpoolAvailable && hasSelectedNonGreenpoolItem)) {
      setActiveStep('PRODUCT_SELECT')
      return
    }
    if (
      data.greenpoolTypeId === 'OPTIONAL' &&
      !(hasSelectedGreenpoolItem || hasSelectedNonGreenpoolItem)
    ) {
      setActiveStep('GREENPOOL_INFO')
      return
    }
  }, [data, setActiveStep])

  const calculatedOrderTotalCost = useMemo(() => {
    if (data?.isLocked) return 0
    return (
      data?.products.reduce((acc, product) => {
        const hasSelectedGreenpool = hasGreenpoolItem(policyId)
        if (hasSelectedGreenpool) return 0

        const selectedIds = [...(selectedProductIds || []), ...(selectedAccessoriesIds || [])]
        if (selectedIds.length === 0) return 0
        if (selectedIds.includes(product.id)) {
          const productQuantityById = qtyOf(policyId, product.id)
          // Disabling as it's a reduce func
          // eslint-disable-next-line no-param-reassign
          acc += product.price * productQuantityById
        }

        return acc
      }, 0) || 0
    )
  }, [data, selectedProductIds, selectedAccessoriesIds, policyId, qtyOf, hasGreenpoolItem])

  useEffect(() => {
    setOrderTotalCost(calculatedOrderTotalCost)
  }, [calculatedOrderTotalCost, setOrderTotalCost])

  if (isLoading) return <LoaderCircle />
  if (!data) return null

  let customDesc
  if (activeStep === 'ACCESSORIES') {
    customDesc = data.stepAccessoriesDescription
  } else if (activeStep === 'GREENPOOL_PRODUCT_SELECT') {
    customDesc = data.stepGreenpoolDescription
  } else {
    customDesc = data.stepMainDescription
  }

  return (
    <>
      {activeStep === 'GREENPOOL_INFO' ? (
        <h2 className="!mb-2 !mt-0 text-2xl">
          <Translation id="Greenpool" />
        </h2>
      ) : (
        <>
          <h2 className="!m-0 sm:!mb-2">
            <StyledText
              colorMode="black"
              size="xl"
              fontWeight="bold"
              className="mobile:!text-xl whitespace-pre-wrap !text-lg !leading-normal md:!text-2xl"
            >
              {data.name || (
                <Translation id="Ecom Policy #{ecomPolicyId}" ecomPolicyId={policyId} />
              )}
            </StyledText>
          </h2>

          {Boolean(!data.isLocked && data?.isGrossSalaryScheme) && (
            <div className="flex w-full justify-center">
              <Badge type="neutral">
                <Translation
                  id="Gross salary deduction"
                  className="text-center leading-none max-sm:!text-sm sm:leading-normal"
                />
              </Badge>
            </div>
          )}

          {customDesc != null && Boolean(customDesc) && (
            <InfoBox className="mt-1 flex flex-col sm:my-2">
              <ExpandableDescription
                description={customDesc}
                showFullText={false}
                TranslationComponent={Translation as ComponentType}
              />
            </InfoBox>
          )}
        </>
      )}

      {activeStep === 'GREENPOOL_INFO' && <GreenpoolInfo />}
      {activeStep === 'GREENPOOL_PRODUCT_SELECT' && <GreenpoolProductsGrid data={data} />}
      {activeStep === 'PRODUCT_SELECT' && <ProductGroupGrid data={data} />}
      {activeStep === 'ACCESSORIES' && <AccessoriesProductGroupGrid data={data} />}
    </>
  )
}
